import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { useSelectionStore } from '@/store/selectionStore';
import { useModalStore } from '@/store/modalStore';
import { checkDeviceOwnership } from '@/api/device/useDevice';
import { useConnectionActions } from '@/connections/hooks/useConnectionActions';
import type { Device } from '@packages/types/device/dto';
import type { DeviceInfo, DeviceConfiguration } from '@packages/types/device/lite';

interface UseDeviceValidationParams {
  devices: Device[];
}

export function useDeviceValidation({ devices }: UseDeviceValidationParams) {
  const { t } = useTranslation('datalog');
  const {
    selectedDeviceId,
    selectedSiteId,
    selectedCompanyId,
    setSelectedDeviceId,
    setSelectedSiteId,
    setSelectedCompanyId,
  } = useSelectionStore();
  const { showConfirm, showAlert } = useModalStore();
  const { handleDisconnect } = useConnectionActions();

  const validateDeviceOwnership = useCallback(
    async (deviceInfo: DeviceInfo, _deviceConfig: DeviceConfiguration) => {
      const serialNumber = deviceInfo.serial_number as string;

      const deviceInCurrentSite = devices.find((d: any) => d.serial_number === serialNumber);
      if (deviceInCurrentSite) {
        if (selectedDeviceId !== deviceInCurrentSite.id) {
          setSelectedDeviceId(deviceInCurrentSite.id);
        }
        return;
      }

      let ownership;
      try {
        ownership = await checkDeviceOwnership(serialNumber);
      } catch (error) {
        console.error('❌ [DatalogPage] checkDeviceOwnership failed:', error);
        return;
      }

      if (!ownership.registered) {
        return;
      }

      const info = ownership.ownership!;

      if (!info.is_same_company) {
        showAlert(
          t(i18nKeys.site.deviceBelongsToOtherCompany),
          t(i18nKeys.site.deviceBelongsToOtherCompanyDesc),
          () => { handleDisconnect(); },
        );
        return;
      }

      if (!info.is_managed_site) {
        showConfirm(
          t(i18nKeys.site.deviceBelongsToUnmanagedSite),
          t(i18nKeys.site.deviceBelongsToUnmanagedSiteDesc),
          () => { handleDisconnect(); },
          () => { handleDisconnect(); },
        );
        return;
      }

      if (info.site_id !== selectedSiteId) {
        showConfirm(
          t(i18nKeys.site.deviceBelongsToAnotherSite),
          t(i18nKeys.site.deviceBelongsToAnotherSiteDesc, { siteName: info.site_name }),
          () => {
            setSelectedSiteId(info.site_id);
            if (info.company_id !== selectedCompanyId) {
              setSelectedCompanyId(info.company_id);
            }
          },
          () => {
            handleDisconnect();
          },
        );
      }
    },
    [
      devices,
      selectedDeviceId,
      selectedSiteId,
      selectedCompanyId,
      setSelectedDeviceId,
      setSelectedSiteId,
      setSelectedCompanyId,
      showConfirm,
      showAlert,
      handleDisconnect,
      t,
    ]
  );

  return { validateDeviceOwnership };
}
